import { ENTITY_CONFIGS } from './EntityConfigs';
import { useBackend } from '@/hooks/useBackend';
import { EntityType } from '@/types/customizing';
import { invalidateCachedOptions } from '@/lib/options/optionsCache';
import { OptionKey, derivedKeys } from '@/lib/options/optionTypes';
import {
  createEmptyInstrumentMetadataConfig,
  normalizeMetadataConfigForInstrumentSave,
  type InstrumentMetadataKind,
} from '@/lib/fileMetadata/instrumentMetadataConfig';
import { dedupeCollaboratorsByUserId } from '@/lib/users/selectableUsers';

export type EntityLoadOptions = {
  /** When false, load in-house staff only (ResearchGroup.List without User.List). */
  includeExternalUsers?: boolean;
};

export class EntityDataService {
  private backend: ReturnType<typeof useBackend>;

  constructor(backend: ReturnType<typeof useBackend>) {
    this.backend = backend;
  }

  private async getTechniqueMap(): Promise<Map<string, string>> {
    const synthesisTechniques = await this.backend.synthesisTechnique.get(undefined);
    return new Map(synthesisTechniques.map((technique: any) => [technique.id, technique.name]));
  }

  async loadData(entityType: EntityType, options?: EntityLoadOptions): Promise<any[]> {
    let response: any[] = [];

    switch (entityType) {
      case 'users': {
        const includeExternal = options?.includeExternalUsers ?? true;
        const externalPromise = includeExternal ? this.backend.user.getUsers(true) : Promise.resolve([]);
        const [externalUsers, internalStaff] = await Promise.all([externalPromise, this.backend.user.getStaff(true)]);

        // Add a type field to distinguish between external and internal users
        const externalUsersWithType = externalUsers.map((item: any) => ({
          ...item,
          userType: 'external',
          roleIds: Array.isArray(item.roleIds) ? item.roleIds[0] || '' : item.roleIds,
        }));

        const internalStaffWithType = internalStaff.map((item: any) => ({
          ...item,
          userType: 'internal',
          roleIds: Array.isArray(item.roleIds) ? item.roleIds[0] || '' : item.roleIds,
        }));

        // Combine all users (staff first so internal wins when the same id appears in both lists)
        const allUsers = dedupeCollaboratorsByUserId([...internalStaffWithType, ...externalUsersWithType]);

        // Create a map of user IDs to names for faculty resolution
        const userMap = new Map(
          allUsers.map((user) => [user.id, `${user.firstName || ''} ${user.lastName || ''}`.trim()])
        );

        response = allUsers.map((user: any) => ({
          ...user,
          facultyName: user.faculty ? userMap.get(user.faculty) || user.faculty : '',
        }));
        break;
      }
      case 'roles':
        response = await this.backend.role.getRolesWithPrivileges();
        break;
      case 'apiKeys':
        response = await this.backend.apiKey.list(true);
        break;
      case 'materials':
        response = await this.backend.material.list(undefined, true);
        break;
      case 'rawMaterials': {
        response = await this.backend.bulkRawMaterial.list();

        // Load materials to resolve materialID to material label
        const materials = await this.backend.material.list(undefined, true);
        const materialMap = new Map(
          materials.map((material: any) => [material.id, material.label || material.name || ''])
        );

        // Load synthesis techniques to resolve technique IDs to names for display
        const techniqueMap = await this.getTechniqueMap();

        // Resolve material labels and store both technique IDs and names for display
        response = response.map((item: any) => ({
          ...item,
          materialLabel: item.materialID ? materialMap.get(item.materialID) || '' : '',
          // Keep original techniques array (IDs) for editing, and create display version
          techniquesDisplay: Array.isArray(item.techniques)
            ? item.techniques.map((techniqueId: string) => techniqueMap.get(techniqueId) || techniqueId).join(', ')
            : '',
          // Ensure techniques is always an array for the form
          techniques: Array.isArray(item.techniques) ? item.techniques : item.techniques || [],
        }));
        break;
      }
      case 'bulkElements':
        response = await this.backend.bulkElement.list();
        break;
      case 'synthesisTechniques':
        response = await this.backend.synthesisTechnique.get(undefined);
        // Format instrumentTypes array for display
        response = response.map((item: any) => ({
          ...item,
          instrumentTypes: Array.isArray(item.instrumentTypes) ? item.instrumentTypes.join(', ') : item.instrumentTypes,
        }));
        break;
      case 'synthesisInstrumentTypes': {
        response = await this.backend.synthesisInstrumentType.list();

        // Load synthesis techniques to resolve technique IDs to names
        const techniqueMap = await this.getTechniqueMap();

        // Format synthesisTechniques array for display and resolve technique names
        response = response.map((item: any) => ({
          ...item,
          synthesisTechniques: Array.isArray(item.synthesisTechniques)
            ? item.synthesisTechniques
                .map((techniqueId: string) => techniqueMap.get(techniqueId) || techniqueId)
                .join(', ')
            : item.synthesisTechniques,
        }));
        break;
      }
      case 'synthesisInstruments':
        response = await this.backend.synthesisInstrument.list(undefined, false, true);

        // Load faculty/staff data to resolve faculty names
        const staffData = await this.backend.user.getStaff(true);
        const facultyMap = new Map(
          staffData.map((user: any) => [user.id, `${user.firstName || ''} ${user.lastName || ''}`.trim()])
        );

        // Format synthesisTechniques array for display and resolve faculty names
        response = response.map((item: any) => ({
          ...item,
          synthesisTechniques: Array.isArray(item.synthesisTechniques)
            ? item.synthesisTechniques.join(', ')
            : item.synthesisTechniques,
          facultyDisplayName: item.faculty ? facultyMap.get(item.faculty) || item.faculty : '',
          // Map instrumentManagers to managers for consistency with entity config
          managers: item.instrumentManagers || [],
          managersDisplayName: Array.isArray(item.instrumentManagers)
            ? item.instrumentManagers.map((managerId: string) => facultyMap.get(managerId) || managerId).join(', ')
            : '',
        }));
        break;
      case 'characterizationTechniques':
        response = await this.backend.characterizationTechnique.list();
        break;
      case 'characterizationInstruments':
        response = await this.backend.characterizationInstrument.list(true);
        break;
      case 'simulationMethods':
        // This requires a technique parameter
        response = await this.backend.synthesisInstrument.list('SIM', false, false);

        // Load staff data to resolve manager names
        const simulationStaff = await this.backend.user.getStaff(true);
        const simulationManagerMap = new Map(
          simulationStaff.map((user: any) => [user.id, `${user.firstName || ''} ${user.lastName || ''}`.trim()])
        );

        // Resolve manager names
        response = response.map((item: any) => ({
          ...item,
          // Map instrumentManagers to managers for consistency with entity config
          managers: item.instrumentManagers || [],
          managersDisplayName: Array.isArray(item.instrumentManagers)
            ? item.instrumentManagers
                .map((managerId: string) => simulationManagerMap.get(managerId) || managerId)
                .join(', ')
            : '',
        }));
        break;
      case 'reportingCategories':
        // Include usage information for customizing entity page
        response = await this.backend.sample.listReportingCategories(true);
        break;
      case 'instrumentFunding':
        // Include usage information for customizing entity page
        response = await this.backend.synthesisInstrument.listInstrumentFunding(true);
        break;
      case 'projectTypes':
        // Include usage information for customizing entity page
        response = await this.backend.project.listProjectTypes(true);
        break;
      default:
        response = [];
    }

    // Add unique IDs for DataGrid
    const finalResponse = response.map((item: any, index: number) => ({
      ...item,
      id: item.id || index,
    }));

    return finalResponse;
  }

  /**
   * Load a single record for editing, processing array fields appropriately
   */
  async loadRecordForEditing(
    entityType: EntityType,
    recordId: string | number,
    options?: EntityLoadOptions
  ): Promise<any> {
    const allData = await this.loadData(entityType, options);
    const record = allData.find((item: any) => item.id === recordId || item.id === String(recordId));

    if (!record) {
      throw new Error(`Record with ID ${recordId} not found`);
    }

    // Process the record to convert display format back to form format
    return this.processRecordForEditing(entityType, record);
  }

  async deleteRecord(entityType: EntityType, recordId: string | number): Promise<void> {
    switch (entityType) {
      case 'users':
        await this.backend.user.delete(String(recordId));
        break;
      case 'apiKeys':
        await this.backend.apiKey.delete(Number(recordId));
        break;
      case 'rawMaterials':
        await this.backend.bulkRawMaterial.delete(Number(recordId));
        break;
      case 'bulkElements':
        await this.backend.bulkElement.delete(Number(recordId));
        break;
      case 'characterizationTechniques':
        await this.backend.characterizationTechnique.delete(Number(recordId));
        break;
      case 'materials':
        await this.backend.material.delete(Number(recordId));
        break;
      case 'roles':
        await this.backend.role.delete(Number(recordId));
        break;
      case 'reportingCategories':
        await this.backend.sample.deleteReportingCategory(Number(recordId));
        break;
      case 'instrumentFunding':
        await this.backend.synthesisInstrument.deleteInstrumentFunding(Number(recordId));
        break;
      case 'projectTypes':
        await this.backend.project.deleteProjectType(Number(recordId));
        break;
      // Entities that don't support deletion:
      // synthesisInstruments: No delete method in API
      // - synthesisInstrumentTypes: No delete method in API
      // - synthesisTechniques: No delete method in API
      // - characterizationInstruments: Not implemented yet
      // - simulationMethods: Not implemented yet
      default:
        throw new Error(`Delete not implemented for entity type: ${entityType}`);
    }
  }

  /**
   * Process record data for editing to convert display format back to form format
   * Converts comma-separated strings back to arrays for multiselect fields
   */
  private processRecordForEditing(entityType: EntityType, record: any): any {
    const entityConfig = ENTITY_CONFIGS[entityType];
    if (!entityConfig) {
      return record;
    }

    const processedRecord = { ...record };

    // Handle special field mappings for editing
    if (entityType === 'simulationMethods' && record.managers) {
      // The managers field should already be an array from our data loading
      // No additional processing needed for the mapping
    }

    // Process each field based on its type
    entityConfig.fields.forEach((field) => {
      if (field.type === 'multiselect-autocomplete' || field.type === 'multiselect') {
        const value = processedRecord[field.name];
        // If the value is a comma-separated string, convert back to array
        if (typeof value === 'string' && value.includes(',')) {
          processedRecord[field.name] = value
            .split(',')
            .map((item: string) => item.trim())
            .filter(Boolean);
        } else if (value === '' || value === null || value === undefined) {
          processedRecord[field.name] = [];
        } else if (!Array.isArray(value)) {
          // If it's a single value, convert to array
          processedRecord[field.name] = [value];
        }
      }
    });

    if (entityType === 'characterizationInstruments' || entityType === 'synthesisInstruments') {
      const kind: InstrumentMetadataKind =
        entityType === 'characterizationInstruments' ? 'characterization' : 'synthesis';
      const instrumentId = String(processedRecord.id ?? '');
      if (!processedRecord.metadataConfig && instrumentId) {
        processedRecord.metadataConfig = createEmptyInstrumentMetadataConfig(kind, instrumentId);
      }
    }

    return processedRecord;
  }

  /**
   * Process record data to ensure array fields are properly formatted
   * Converts empty strings to empty arrays for multiselect fields
   */
  private processRecordData(entityType: EntityType, record: any): any {
    const entityConfig = ENTITY_CONFIGS[entityType];
    if (!entityConfig) {
      return record;
    }

    const processedRecord = { ...record };

    // Process each field based on its type
    entityConfig.fields.forEach((field) => {
      if (field.type === 'multiselect-autocomplete' || field.type === 'multiselect') {
        const value = processedRecord[field.name];
        // If the value is an empty string or null/undefined, convert to empty array
        if (value === '' || value === null || value === undefined) {
          processedRecord[field.name] = [];
        } else if (!Array.isArray(value)) {
          // If it's not an array but should be, convert to array
          processedRecord[field.name] = [value];
        }
      }
    });

    if (entityType === 'characterizationInstruments' || entityType === 'synthesisInstruments') {
      const kind: InstrumentMetadataKind =
        entityType === 'characterizationInstruments' ? 'characterization' : 'synthesis';
      const instrumentId = String(processedRecord.id ?? '');
      processedRecord.metadataConfig = normalizeMetadataConfigForInstrumentSave(
        processedRecord.metadataConfig,
        kind,
        instrumentId
      );
    }

    return processedRecord;
  }

  async saveRecord(entityType: EntityType, record: any, isEdit: boolean): Promise<unknown> {
    // Process the record data to ensure proper array formatting
    const processedRecord = this.processRecordData(entityType, record);

    // Determine which base cache keys to invalidate based on entity type
    const getBaseCacheKeysToInvalidate = (entityType: EntityType): OptionKey[] => {
      // Extract base keys from derivedKeys for reference
      const baseKeysFromDerived = new Set<OptionKey>();
      for (const [, baseKeys] of derivedKeys.entries()) {
        baseKeys.forEach((key: OptionKey) => baseKeysFromDerived.add(key));
      }

      // Map entity types to their corresponding option keys
      const entityToOptionKeys: Record<EntityType, OptionKey[]> = {
        users: ['users', 'staff'], // Special case: affects both base keys
        synthesisInstruments: ['synthesisInstruments'],
        synthesisTechniques: ['synthesisTechniques'],
        synthesisInstrumentTypes: ['synthesisInstrumentTypes'],
        instrumentFunding: [],
        characterizationInstruments: ['characterizationInstruments'],
        characterizationTechniques: ['characterizationTechniques'],
        materials: ['materials'],
        roles: ['roles'], // Used as base key in derivedKeys
        reportingCategories: ['sampleReportingCategory'],
        projectTypes: ['projectType'],
        // These entity types don't have corresponding option keys:
        rawMaterials: [],
        apiKeys: [],
        simulationMethods: [],
        bulkElements: [],
      };

      return entityToOptionKeys[entityType] || [];
    };

    const cacheKeysToInvalidate = getBaseCacheKeysToInvalidate(entityType);

    switch (entityType) {
      case 'users':
        // Convert single value of roleId to array for the API
        // That is a halfway implemented feature, users with multiple roles are not actually supported.
        // Only for the current users, this is used to add admin role if they are allowed to become admin.
        // Not needed here at all.
        if (processedRecord.roleIds && !Array.isArray(processedRecord.roleIds)) {
          processedRecord.roleIds = [processedRecord.roleIds];
        }

        // Remove the userType field before sending to API (backend determines type based on role)
        delete processedRecord.userType;

        if (isEdit) {
          return await this.backend.user.update(processedRecord.id, processedRecord);
        }

        return await this.backend.user.create(processedRecord);
      case 'apiKeys':
        if (isEdit) {
          await this.backend.apiKey.update(processedRecord.id, processedRecord);
        } else {
          await this.backend.apiKey.create(processedRecord);
        }
        break;
      case 'rawMaterials':
        if (isEdit) {
          await this.backend.bulkRawMaterial.update(processedRecord.id, processedRecord);
        } else {
          await this.backend.bulkRawMaterial.create(processedRecord);
        }
        break;
      case 'bulkElements':
        if (isEdit) {
          await this.backend.bulkElement.update(processedRecord.id, processedRecord);
        } else {
          await this.backend.bulkElement.create(processedRecord);
        }
        break;
      case 'synthesisInstrumentTypes':
        if (isEdit) {
          await this.backend.synthesisInstrumentType.update(processedRecord.id, processedRecord);
        } else {
          await this.backend.synthesisInstrumentType.create(processedRecord);
        }
        break;
      case 'synthesisTechniques':
        if (isEdit) {
          await this.backend.synthesisTechnique.update(processedRecord.id, processedRecord);
        } else {
          await this.backend.synthesisTechnique.create(processedRecord);
        }
        break;
      case 'synthesisInstruments':
        // Map managers field back to instrumentManagers for the API (if present)
        const synthesisRecord = { ...processedRecord };
        if (synthesisRecord.managers) {
          synthesisRecord.instrumentManagers = synthesisRecord.managers;
          delete synthesisRecord.managers; // Remove the mapped field
        }
        if (isEdit) {
          await this.backend.synthesisInstrument.update(synthesisRecord.id, synthesisRecord);
        } else {
          await this.backend.synthesisInstrument.create(synthesisRecord);
        }
        break;
      case 'simulationMethods':
        // Map managers field back to instrumentManagers for the API
        const simulationRecord = { ...processedRecord };
        if (simulationRecord.managers) {
          simulationRecord.instrumentManagers = simulationRecord.managers;
          delete simulationRecord.managers; // Remove the mapped field
        }
        if (isEdit) {
          await this.backend.synthesisInstrument.update(simulationRecord.id, simulationRecord);
        } else {
          await this.backend.synthesisInstrument.create(simulationRecord);
        }
        break;
      case 'characterizationTechniques':
        if (isEdit) {
          await this.backend.characterizationTechnique.update(processedRecord.id, processedRecord);
        } else {
          await this.backend.characterizationTechnique.create(processedRecord);
        }
        break;
      case 'characterizationInstruments':
        if (isEdit) {
          await this.backend.characterizationInstrument.update(processedRecord.id, processedRecord);
        } else {
          await this.backend.characterizationInstrument.create(processedRecord);
        }
        break;
      case 'materials':
        if (isEdit) {
          await this.backend.material.update(processedRecord.id, processedRecord);
        } else {
          await this.backend.material.create(processedRecord);
        }
        break;
      case 'roles':
        if (isEdit) {
          await this.backend.role.update(processedRecord.id, processedRecord);
        } else {
          await this.backend.role.create(processedRecord);
        }
        break;
      case 'reportingCategories':
        if (isEdit) {
          await this.backend.sample.updateReportingCategory(processedRecord.id, processedRecord);
        } else {
          await this.backend.sample.createReportingCategory(processedRecord);
        }
        break;
      case 'instrumentFunding':
        if (isEdit) {
          // Note: Update is not yet implemented in the backend API
          throw new Error('Update not yet implemented for instrument funding');
        } else {
          await this.backend.synthesisInstrument.createInstrumentFunding(processedRecord);
        }
        break;
      case 'projectTypes':
        if (isEdit) {
          await this.backend.project.updateProjectType(processedRecord.id, processedRecord);
        } else {
          await this.backend.project.createProjectType(processedRecord);
        }
        break;
      // Add other entity types as needed
      default:
        throw new Error(`Save not implemented for entity type: ${entityType}`);
    }

    // Invalidate relevant cache entries after successful save
    if (cacheKeysToInvalidate.length > 0) {
      invalidateCachedOptions(cacheKeysToInvalidate);
    }
  }
}
